import { Fragment, useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import CardItemValue from "components/TableDetails/components/CardItemValue";
import { TitleInfo } from "components/TableDetails/components/TitleInfo";
import { CARD_ITEMS, CARDS } from "components/TableDetails/DeviceDetails/types";
import { renderAvailableData } from "components/TableDetails/functions";
import TableContext from "context/table/tableContext";
import {
    ENDPOINTS,
    renderCanOemFileVehicle,
    sortAndReturnArray,
    TASK_TYPES,
    useApi
} from "shared";

import { CREATED_AT } from "../../constants";
import { getDateAfterSomeTime } from "../../functions";
import { LimitWarningBanner } from "../../shared";
import { CanSettings, DetailsData } from "../../types";

const Can = () => {
    const { detailsData } = useContext(TableContext);

    const { t } = useTranslation();
    const { getData, handleResponse } = useApi();

    const [autoscanLimitDate, setAutoscanLimitDate] = useState<string | null>(
        null
    );

    useEffect(() => {
        const getLatestRxAutoscanTasks = async () => {
            try {
                const settingsResponse = await getData(ENDPOINTS.Settings);

                const canSettings: CanSettings =
                    settingsResponse.data.common_settings.can;

                const {
                    tasks_count_limit_within_period,
                    tasks_limit_period_hours
                } = canSettings;

                const { data } = await getData(
                    `${ENDPOINTS.Devices}/${imei}/latestDeviceTasks?task_type=${TASK_TYPES.RxAutoscan}&period_hours=${tasks_limit_period_hours}`
                );

                if (data.length >= tasks_count_limit_within_period) {
                    const sortedData = sortAndReturnArray(data, CREATED_AT);
                    const oldestData: string = sortedData[0][CREATED_AT];

                    const dateAfter24Hours = getDateAfterSomeTime(
                        oldestData,
                        tasks_limit_period_hours,
                        "hours"
                    );

                    setAutoscanLimitDate(dateAfter24Hours);
                }
            } catch (error) {
                handleResponse(error);
            }
        };

        getLatestRxAutoscanTasks();
        // eslint-disable-next-line
    }, []);

    const {
        imei,
        can,
        can_package: assignedCanPackage
    }: DetailsData = detailsData || {};

    const getCanOemFileVehicleValue = () => {
        if (can) {
            const { vehicle, vehicle_id } = can;

            return renderCanOemFileVehicle(vehicle, vehicle_id, t);
        }

        return null;
    };

    const getCanOemFilePackageValue = () => {
        if (can) {
            const { can_package, can_package_id } = can;

            if (can_package) {
                return can_package.name;
            }

            if (can_package_id !== null) {
                return `${t("General##unknown")} (${t("General##id")}: ${can_package_id})`;
            }

            return null;
        }

        return null;
    };

    const canOemFileVehicle = getCanOemFileVehicleValue();
    const canOemFilePackage = getCanOemFilePackageValue();

    const list = [
        {
            title: CARD_ITEMS.Firmware,
            value: can?.chip_version,
            isAvailable: Boolean(can?.chip_version)
        },
        {
            title: CARD_ITEMS.Dtb,
            value: can?.dtb_version,
            isAvailable:
                can?.dtb_version !== null && can?.dtb_version !== undefined
        },
        {
            title: CARD_ITEMS.AssignedPackage,
            value: assignedCanPackage?.name,
            isAvailable:
                assignedCanPackage !== null && assignedCanPackage !== undefined
        },
        {
            title: CARD_ITEMS.CanOemFileVehicle,
            value: canOemFileVehicle,
            isAvailable: canOemFileVehicle !== null
        },
        {
            title: CARD_ITEMS.CanOemFilePackage,
            value: canOemFilePackage,
            isAvailable: canOemFilePackage !== null
        }
    ];

    return (
        <div>
            <div>
                {t(`General##${CARDS.Can}`)}

                <TitleInfo
                    aria-label={t(`General##${CARDS.Can}`)}
                    cardName={CARDS.Can}
                    list={list}
                />
            </div>

            {autoscanLimitDate !== null && (
                <LimitWarningBanner
                    testId="can-autoscan-limit-banner"
                    title={t("Table##exausted autoscan limit")}
                    date={autoscanLimitDate}
                />
            )}

            <div>
                {renderAvailableData(list).map(({ title, value }) => {
                    return (
                        <Fragment key={title}>
                            <span>{t(`Table##${title}`)}:</span>
                            <CardItemValue value={value} />
                        </Fragment>
                    );
                })}
            </div>
        </div>
    );
};

export { Can };
